/**
 * Placeholder — visually flags text that comes from the Phase-1 templated
 * narrative layer (`config/placeholderNarrative.ts`) rather than from real
 * AI output. Adir gets a subtle amber-dashed underline + hover tooltip on
 * anything that will be regenerated by `MarketerViewAIService` in Phase 2.
 *
 * In Phase 2 we either:
 *   (a) Stop wrapping with <Placeholder> at the render site, OR
 *   (b) Keep it as a no-op for backwards-compat (delete this file and replace
 *       with a passthrough during the swap-out).
 *
 * Use `block` when wrapping a whole paragraph; default is inline span.
 */

import React from 'react';

const TOOLTIP =
  'Templated narrative (Phase 1), replaced by MarketerViewAI in Phase 2.';

export interface PlaceholderProps {
  children: React.ReactNode;
  /** Render as a block element (e.g. wrapping a <p>). Default is inline span. */
  block?: boolean;
  /** Custom inline styles to merge after the marker styles. */
  style?: React.CSSProperties;
}

export const Placeholder: React.FunctionComponent<PlaceholderProps> = ({
  children,
  block = false,
  style,
}) => {
  const Tag = block ? 'div' : 'span';
  return (
    <Tag
      title={TOOLTIP}
      data-narrative-placeholder
      style={{
        borderBottom: '1px dashed rgba(245, 158, 11, 0.55)',
        cursor: 'help',
        ...style,
      }}
    >
      {children}
    </Tag>
  );
};

/**
 * Small page-level badge indicating that narrative copy on this page is
 * Phase-1 templated. Rendered once near the page chrome.
 */
export const PlaceholderPageBadge: React.FunctionComponent = () => (
  <span
    title={TOOLTIP}
    data-narrative-placeholder-badge
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: '#B45309',
      background: '#FEF3C7',
      border: '1px dashed rgba(245, 158, 11, 0.55)',
      padding: '3px 10px',
      borderRadius: 999,
      cursor: 'help',
      whiteSpace: 'nowrap',
    }}
  >
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#F59E0B' }} />
    Templated narrative · Phase 1
  </span>
);
